import * as Yup from "yup";
import { Dayjs } from "dayjs";

import { IServiceHours } from "@/interfaces/service-hours.interface";
import { ConfigurationTabMessages } from "@/messages/customer/configuration-tab.messages";

import {
  formatTimeForApi,
  getWeekDayDescription,
  parseTimeFromApi,
} from "../../constants";

export type ServiceHoursFormValues = {
  weekDay: number | null;
  description: string;
  startOperationHour: Dayjs | null;
  endOperationHour: Dayjs | null;
};

export const buildServiceHoursInitialData = (
  messages: ConfigurationTabMessages,
  selected?: IServiceHours
): ServiceHoursFormValues => {
  if (!selected) {
    return {
      weekDay: null,
      description: "",
      startOperationHour: null,
      endOperationHour: null,
    };
  }

  return {
    weekDay: selected.weekDay,
    description: selected.description,
    startOperationHour: parseTimeFromApi(selected.startOperationHour),
    endOperationHour: parseTimeFromApi(selected.endOperationHour),
  };
};

export const buildServiceHoursFormSchema = (messages: ConfigurationTabMessages) =>
  Yup.object().shape({
    weekDay: Yup.number().nullable().required(messages.errorRequiredWeekDay),
    description: Yup.string().required(messages.errorRequiredDescription).max(50),
    startOperationHour: Yup.mixed().nullable().required(messages.errorRequiredStartHour),
    endOperationHour: Yup.mixed()
      .nullable()
      .required(messages.errorRequiredEndHour)
      .test("is-after-start", messages.errorEndHourAfterStart, function validateEndHour(value) {
        const { startOperationHour } = this.parent as ServiceHoursFormValues;
        if (!startOperationHour || !value) return true;
        return (value as Dayjs).isAfter(startOperationHour);
      }),
  });

export const mapServiceHoursFormToInput = (
  values: ServiceHoursFormValues,
  messages: ConfigurationTabMessages
) => ({
  weekDay: values.weekDay as number,
  description:
    values.description.trim() ||
    getWeekDayDescription(values.weekDay as number, messages),
  startOperationHour: formatTimeForApi(values.startOperationHour),
  endOperationHour: formatTimeForApi(values.endOperationHour),
});

export type ServiceHoursBulkFormValues = {
  selectedWeekDays: number[];
  startOperationHour: Dayjs | null;
  endOperationHour: Dayjs | null;
};

export const buildServiceHoursBulkInitialData = (): ServiceHoursBulkFormValues => ({
  selectedWeekDays: [],
  startOperationHour: null,
  endOperationHour: null,
});

export const buildServiceHoursBulkFormSchema = (messages: ConfigurationTabMessages) =>
  Yup.object().shape({
    selectedWeekDays: Yup.array()
      .of(Yup.number())
      .min(1, messages.errorRequiredBulkWeekDays),
    startOperationHour: Yup.mixed().nullable().required(messages.errorRequiredStartHour),
    endOperationHour: Yup.mixed()
      .nullable()
      .required(messages.errorRequiredEndHour)
      .test("is-after-start", messages.errorEndHourAfterStart, function validateEndHour(value) {
        const { startOperationHour } = this.parent as ServiceHoursBulkFormValues;
        if (!startOperationHour || !value) return true;
        return (value as Dayjs).isAfter(startOperationHour);
      }),
  });

export const mapServiceHoursBulkFormToInputs = (
  values: ServiceHoursBulkFormValues,
  messages: ConfigurationTabMessages
) =>
  [...values.selectedWeekDays]
    .sort((a, b) => a - b)
    .map((weekDay) => ({
      weekDay,
      description: getWeekDayDescription(weekDay, messages),
      startOperationHour: formatTimeForApi(values.startOperationHour),
      endOperationHour: formatTimeForApi(values.endOperationHour),
    }));
